/**
 * AGS Data State Management Composable
 * 
 * This composable provides state management for AGS (American Geographic Survey) data
 * following the same patterns as crime data but using the new AGS database tables.
 * 
 * Vue 2 Compatible Implementation
 * Since this project uses Vue 2.6, this composable uses Vue 2 reactive patterns
 * while maintaining the same API structure as Vue 3 composables.
 */

import Vue from 'vue';

// Create a Vue instance to act as our store for proper reactivity
const storeInstance = new Vue({
  data() {
    return {
      selectedCategories: [],
      selectedVariables: [],
      scaleDirections: {}, // Per-variable scale directions (path -> boolean, false = higher is worse)
      weights: {}, // Per-variable weights (path -> decimal 0-1)
      agsDataOpacity: 0.8, // Default opacity (80%) - better visibility
      availableCategories: [],
      agsLegendVisible: false,
      agsScaleReversed: false, // Default: ascending scale (lower is better)
      loading: false,
      error: null
    };
  }
});

// Get the reactive state from the Vue instance
const state = storeInstance.$data;

console.log('🏪 AGS Store: Vue instance store created:', storeInstance, state);

/**
 * AGS Store Composable
 * Provides reactive state management for AGS data selection and visualization
 * 
 * @returns {Object} AGS store interface with state, actions, and computed properties
 */
export function useAGSStore() {

  // Actions for managing selected variables
  const setSelectedVariables = (variables) => {
    if (!Array.isArray(variables)) {
      console.warn('setSelectedVariables expects an array, received:', typeof variables);
      return;
    }
    console.log('🏪 AGS Store: Setting selected variables:', {
      previousCount: storeInstance.$data.selectedVariables.length,
      newCount: variables.length,
      variables: variables,
      storeInstance: storeInstance
    });

    // Update the Vue instance data directly for proper reactivity
    storeInstance.$set(storeInstance.$data, 'selectedVariables', [...variables]);

    // Initialize scale directions for new variables (default: false = higher is worse)
    const newScaleDirections = {};
    variables.forEach(variable => {
      // Preserve existing scale direction if it exists, otherwise default to false
      newScaleDirections[variable.path] = state.scaleDirections[variable.path] ?? false;
    });
    storeInstance.$set(storeInstance.$data, 'scaleDirections', newScaleDirections);

    console.log('🏪 AGS Store: Variables set successfully. Vue instance data:', storeInstance.$data.selectedVariables);
    console.log('🏪 AGS Store: Scale directions initialized:', storeInstance.$data.scaleDirections);
    console.log('🏪 AGS Store: State reference:', state.selectedVariables);
  };

  const addVariable = (variable) => {
    if (!variable || typeof variable !== 'object') {
      console.warn('addVariable expects a variable object, received:', typeof variable);
      return;
    }

    // Check if variable already exists (by path to avoid duplicates)
    const existingIndex = state.selectedVariables.findIndex(v => v.path === variable.path);
    if (existingIndex === -1) {
      state.selectedVariables.push({ ...variable });
    } else {
      // Update existing variable if it already exists
      Vue.set(state.selectedVariables, existingIndex, { ...variable });
    }
  };

  const removeVariable = (variable) => {
    if (!variable) {
      console.warn('removeVariable expects a variable object');
      return;
    }

    const pathToRemove = typeof variable === 'string' ? variable : variable.path;
    state.selectedVariables = state.selectedVariables.filter(v => v.path !== pathToRemove);
  };

  const clearAllVariables = () => {
    state.selectedVariables = [];
  };

  // Actions for managing selected categories
  const setSelectedCategories = (categories) => {
    if (!Array.isArray(categories)) {
      console.warn('setSelectedCategories expects an array, received:', typeof categories);
      return;
    }
    state.selectedCategories = [...categories];
  };

  const addCategory = (category) => {
    if (!category || typeof category !== 'object') {
      console.warn('addCategory expects a category object, received:', typeof category);
      return;
    }

    const existingIndex = state.selectedCategories.findIndex(c => c.id === category.id);
    if (existingIndex === -1) {
      state.selectedCategories.push({ ...category });
    }
  };

  const removeCategory = (category) => {
    if (!category) {
      console.warn('removeCategory expects a category object');
      return;
    }

    const idToRemove = typeof category === 'number' ? category : category.id;
    state.selectedCategories = state.selectedCategories.filter(c => c.id !== idToRemove);
  };

  // Actions for managing available categories
  const setAvailableCategories = (categories) => {
    if (!Array.isArray(categories)) {
      console.warn('setAvailableCategories expects an array, received:', typeof categories);
      return;
    }
    state.availableCategories = [...categories];
  };

  // Actions for managing opacity
  const setAgsDataOpacity = (opacity) => {
    const numericOpacity = parseFloat(opacity);
    if (isNaN(numericOpacity) || numericOpacity < 0.1 || numericOpacity > 1.0) {
      console.warn('setAgsDataOpacity expects a number between 0.1 and 1.0, received:', opacity);
      return;
    }
    state.agsDataOpacity = numericOpacity;
  };

  // Actions for managing legend visibility
  const setAgsLegendVisible = (visible) => {
    state.agsLegendVisible = Boolean(visible);
  };

  const showAgsLegend = () => {
    state.agsLegendVisible = true;
  };

  const hideAgsLegend = () => {
    state.agsLegendVisible = false;
  };

  // Actions for managing scale direction
  const toggleAgsScale = () => {
    state.agsScaleReversed = !state.agsScaleReversed;
  };

  // Actions for managing per-variable scale directions
  const setScaleDirection = (variablePath, higherIsBetter) => {
    if (!variablePath) {
      console.warn('setScaleDirection expects a variable path');
      return;
    }
    Vue.set(state.scaleDirections, variablePath, Boolean(higherIsBetter));
  };

  const toggleScaleDirection = (variablePath) => {
    if (!variablePath) {
      console.warn('toggleScaleDirection expects a variable path');
      return;
    }
    const currentValue = state.scaleDirections[variablePath] ?? false;
    Vue.set(state.scaleDirections, variablePath, !currentValue);
  };

  // Actions for managing loading state
  const setLoading = (loading) => {
    state.loading = Boolean(loading);
  };

  // Actions for managing error state
  const setError = (error) => {
    state.error = error;
    state.loading = false; // Stop loading when error occurs
    console.error('AGS Store Error:', error);
  };

  const clearError = () => {
    state.error = null;
  };

  // Enhanced error handling with retry logic
  const handleError = (error, context = 'AGS operation') => {
    let errorMessage = `${context} failed`;

    if (error && typeof error === 'object') {
      if (error.message) {
        errorMessage = error.message;
      } else if (error.status) {
        errorMessage = `${context} failed with status ${error.status}`;
      }
    } else if (typeof error === 'string') {
      errorMessage = error;
    }

    setError(errorMessage);
    return errorMessage;
  };

  // Utility actions
  const reset = () => {
    state.selectedCategories = [];
    state.selectedVariables = [];
    state.scaleDirections = {};
    state.weights = {};
    state.agsDataOpacity = 0.8;
    state.availableCategories = [];
    state.agsLegendVisible = false;
    state.agsScaleReversed = false;
    state.loading = false;
    state.error = null;
  };

  // Computed properties (Vue 2 compatible - using getters)
  const hasSelectedVariables = () => {
    return state.selectedVariables.length > 0;
  };

  const hasSelectedCategories = () => {
    return state.selectedCategories.length > 0;
  };

  const selectedVariableCount = () => {
    return state.selectedVariables.length;
  };

  const selectedCategoryCount = () => {
    return state.selectedCategories.length;
  };

  // Get variables filtered by search term
  const getFilteredVariables = (searchTerm = '') => {
    if (!searchTerm || typeof searchTerm !== 'string') {
      return state.selectedVariables;
    }

    const term = searchTerm.toLowerCase().trim();
    return state.selectedVariables.filter(variable =>
      (variable.label && variable.label.toLowerCase().includes(term)) ||
      (variable.path && variable.path.toLowerCase().includes(term)) ||
      (variable.description && variable.description.toLowerCase().includes(term))
    );
  };

  // Get variables by category
  const getVariablesByCategory = (categoryId) => {
    if (!categoryId) {
      return [];
    }

    return state.selectedVariables.filter(variable =>
      variable.category_id === categoryId
    );
  };

  // Get selected variables formatted for API requests
  const getSelectedVariablesForAPI = () => {
    return state.selectedVariables.map(variable => ({
      path: variable.path,
      category_id: variable.category_id,
      label: variable.label,
      type: variable.type
    }));
  };

  // Get scale directions formatted for API requests
  const getScaleDirectionsForAPI = () => {
    return { ...state.scaleDirections };
  };

  // Get weights for multi-variable mode
  const getWeightsForAPI = () => {
    const count = state.selectedVariables.length;
    if (count === 0) return {};

    // If custom weights are set, use them
    if (Object.keys(state.weights).length > 0) {
      return { ...state.weights };
    }

    // Otherwise, use equal weighting
    const weight = 1.0 / count;
    const weights = {};
    state.selectedVariables.forEach(variable => {
      weights[variable.path] = weight;
    });
    return weights;
  };

  // Set custom weights
  const setWeights = (weights) => {
    if (!weights || typeof weights !== 'object') {
      console.warn('setWeights expects an object, received:', typeof weights);
      return;
    }
    state.weights = { ...weights };
  };

  // Set weight for a specific variable
  const setVariableWeight = (variablePath, weight) => {
    if (!variablePath) {
      console.warn('setVariableWeight expects a variable path');
      return;
    }
    const numericWeight = parseFloat(weight);
    if (isNaN(numericWeight) || numericWeight < 0 || numericWeight > 1) {
      console.warn('setVariableWeight expects a weight between 0 and 1, received:', weight);
      return;
    }
    Vue.set(state.weights, variablePath, numericWeight);
  };

  /**
   * Get color for a specific AGS intensity level
   * Maps intensity level (string or number) to color based on current scale direction
   * 
   * @param {string|number} intensityLevel - Intensity level (e.g., "very_low", "low", or 0-7)
   * @returns {string} Hex color code
   */
  const getAgsIntensityColor = (intensityLevel) => {
    // Ascending scale colors (default): green → red (higher is worse)
    const defaultColors = ['#059669', '#22c55e', '#84cc16', '#eab308', '#f97316', '#ef4444', '#dc2626', '#9ca3af'];

    // Descending scale colors (reversed): red → green (higher is better)
    const reversedColors = ['#dc2626', '#ef4444', '#f97316', '#eab308', '#84cc16', '#22c55e', '#059669', '#9ca3af'];

    // Map string intensity levels to numeric indices
    const intensityMap = {
      'very_low': 0,
      'low': 1,
      'low_moderate': 2,
      'moderate': 3,
      'moderate_high': 4,
      'high': 5,
      'very_high': 6,
      'no_data': 7
    };

    // Convert string to number if needed
    let level;
    if (typeof intensityLevel === 'string') {
      level = intensityMap[intensityLevel.toLowerCase()] ?? 7; // Default to "no data" if unknown
    } else {
      level = intensityLevel;
    }

    // Determine if we should reverse colors
    let shouldReverse = false;

    if (state.selectedVariables.length === 1) {
      // Single variable mode: use per-variable scale direction
      const variable = state.selectedVariables[0];
      const scaleDirection = state.scaleDirections[variable.path] ?? false;
      // If scaleDirection is true (higher is better), reverse colors
      shouldReverse = scaleDirection;
    } else {
      // Multi-variable mode: use global scale reversed state
      shouldReverse = state.agsScaleReversed;
    }

    const colors = shouldReverse ? reversedColors : defaultColors;

    // Ensure intensity level is within valid range
    level = Math.max(0, Math.min(7, level));
    return colors[level];
  };

  // Get opacity as percentage for display
  const getOpacityPercentage = () => {
    return Math.round(state.agsDataOpacity * 100);
  };

  // Check if a specific variable is selected
  const isVariableSelected = (variablePath) => {
    if (!variablePath) return false;
    return state.selectedVariables.some(v => v.path === variablePath);
  };

  // Check if a specific category is selected
  const isCategorySelected = (categoryId) => {
    if (!categoryId) return false;
    return state.selectedCategories.some(c => c.id === categoryId);
  };

  // Return the composable interface with getters for reactivity
  return {
    // State (reactive) - use getters to ensure we always get current state
    get selectedCategories() {
      return storeInstance.$data.selectedCategories;
    },
    get selectedVariables() {
      console.log('🔍 AGS Store: selectedVariables getter called, returning:', storeInstance.$data.selectedVariables);
      return storeInstance.$data.selectedVariables;
    },
    get scaleDirections() {
      return storeInstance.$data.scaleDirections;
    },
    get weights() {
      return storeInstance.$data.weights;
    },
    get agsDataOpacity() {
      return storeInstance.$data.agsDataOpacity;
    },
    get availableCategories() {
      return storeInstance.$data.availableCategories;
    },
    get agsLegendVisible() {
      return storeInstance.$data.agsLegendVisible;
    },
    get agsScaleReversed() {
      return storeInstance.$data.agsScaleReversed;
    },
    get loading() {
      return storeInstance.$data.loading;
    },
    get error() {
      return storeInstance.$data.error;
    },

    // Variable Actions
    setSelectedVariables,
    addVariable,
    removeVariable,
    clearAllVariables,

    // Category Actions
    setSelectedCategories,
    addCategory,
    removeCategory,
    setAvailableCategories,

    // Opacity Actions
    setAgsDataOpacity,

    // Legend Actions
    setAgsLegendVisible,
    showAgsLegend,
    hideAgsLegend,

    // Scale Actions
    toggleAgsScale,
    setScaleDirection,
    toggleScaleDirection,

    // Weight Actions
    setWeights,
    setVariableWeight,

    // Color Actions
    getAgsIntensityColor,

    // Loading Actions
    setLoading,

    // Error Actions
    setError,
    clearError,
    handleError,

    // Utility Actions
    reset,

    // Computed Properties
    hasSelectedVariables,
    hasSelectedCategories,
    selectedVariableCount,
    selectedCategoryCount,
    getOpacityPercentage,

    // Helper Methods
    getFilteredVariables,
    getVariablesByCategory,
    getSelectedVariablesForAPI,
    getScaleDirectionsForAPI,
    getWeightsForAPI,
    isVariableSelected,
    isCategorySelected
  };
}

// Export a singleton instance for global use if needed
export const agsStore = useAGSStore();

// Export default for easier importing
export default useAGSStore;